'use client';
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */

import { Voucher } from '@ahamove/icons/order';

import { useLocationStore } from '@ahm/react-map/store';
import { Button, Text, Tooltip, rem } from '@mantine/core';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useEffect, useMemo } from 'react';
import IconSuccess from '@/assets/icons/toast-success-icon.svg';
import IconWarningCircle from '@/assets/icons/warning-circle-icon.svg';
import { ModalName, showModal } from '@/components/modals';
import { ROUTES } from '@/config/router';
import { useBookingRequestBodies } from '@/features/booking/hooks/use-booking-request';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function PromoSelection() {
  const tBooking = useTranslations('booking');
  const pathname = usePathname();
  const [paramPromo, setParamPromo] = useQueryState(
    'promo_code',
    parseAsArrayOf(parseAsString).withDefault([])
  );
  // const searchParams = useSearchParams();
  // const paramPromo = searchParams.get('promo_code');
  // const router = useRouter();

  const paths = useLocationStore.use.paths();
  const { estimateRequestBody } = useBookingRequestBodies(pathname, null);

  const {
    estimatedData,
    packageDetail,
    selectedService,
    isLoadingStore,
    errorPromo,
  } = useBookingStore((state) => state);

  const isMobile = useIsMobile();

  const onOpenModalPromos = () => {
    showModal(
      ModalName.Promotion,
      {},
      {
        fullScreen: isMobile,
        centered: true,
        closeOnClickOutside: false,
        closeOnEscape: false,
        onClose: () => {
          //
        },
        padding: 0,
        styles: {
          root: {
            background: '#fff',
            padding: 0,
          },
          header: {
            background: '#fff',
            padding: '0px 24px',
          },
          content: {
            background: '#fff',
            padding: '0px',
          },
          body: {
            padding: '0px',
            background: '#fff',
          },
        },
      }
    );
  };
  const disablePromoSelection = useMemo(() => {
    if (isLoadingStore || !estimatedData) return true;

    switch (pathname) {
      case ROUTES.nationwide:
        return (
          !packageDetail ||
          packageDetail?.length < 1 ||
          paths.length < 2 ||
          !selectedService?._id
        );

      case ROUTES.home:
      case ROUTES.newOrder:
      case ROUTES.truck:
      case ROUTES.houseMoving:
        return paths.length < 2 || !selectedService?._id;

      default:
        return true;
    }
  }, [
    isLoadingStore,
    estimatedData,
    pathname,
    packageDetail,
    paths.length,
    selectedService?._id,
  ]);

  const promoError = useMemo(() => {
    const discountDetails = estimatedData?.data?.discount_details;
    const hasDiscountError = discountDetails
      ? discountDetails.some((detail: any) => !detail.is_valid)
      : false;

    if (
      estimatedData?.data?.promo_error_message ||
      estimatedData?.error?.description ||
      hasDiscountError ||
      errorPromo
    )
      return true;
    return false;
  }, [estimatedData, errorPromo]);
  const unsetPromo = useMemo(() => {
    if (!estimatedData?.data?.promo_error_message && !paramPromo) return true;
    return false;
  }, [estimatedData, paramPromo]);

  const totalPromosSelected = useMemo(
    () => (paramPromo ? paramPromo.length : 0),
    [paramPromo]
  );

  useEffect(() => {
    if (
      !paramPromo.length &&
      estimatedData?.data?.suggested_promotion &&
      estimateRequestBody.suggest_promotion
    ) {
      // router.replace(
      //   `${pathname}?promo_code=${estimatedData?.data.suggested_promotion}`
      // );
      void setParamPromo([estimatedData?.data.suggested_promotion]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimatedData]);

  return (
    <Tooltip
      multiline
      position="top-start"
      w={194}
      radius={8}
      arrowPosition="side"
      px={12}
      py={10}
      disabled={disablePromoSelection || unsetPromo}
      label={
        promoError
          ? tBooking('promo.notify_promo_code_not_applicable')
          : tBooking('promo.notify_promo_code_apply_success')
      }
      withArrow
      arrowOffset={10}
      arrowSize={8}
    >
      <Button
        variant={isMobile ? 'white' : 'default'}
        disabled={disablePromoSelection}
        px={isMobile ? rem(4) : rem(12)}
        py={rem(8)}
        h={rem(40)}
        fullWidth
        styles={{
          inner: {
            justifyContent: isMobile ? 'center' : 'flex-start',
            width: '100%',
          },
        }}
        leftSection={
          !paramPromo.length ? (
            <Voucher />
          ) : promoError && paramPromo ? (
            <IconWarningCircle className="size-6" />
          ) : (
            <IconSuccess className="size-6" />
          )
        }
        onClick={() => {
          onOpenModalPromos();
        }}
      >
        <Text
          maw={estimatedData?.data?.promo_error_message ? 115 : '100%'}
          truncate={!estimatedData?.data?.promo_error_message}
          lineClamp={1}
          size="sm"
          fw={isMobile ? 700 : 500}
          c="neutral.7"
        >
          {totalPromosSelected === 1 && !promoError
            ? paramPromo[0]
            : `${tBooking('promo.promotion_code_content')}${totalPromosSelected ? ` (${totalPromosSelected})` : ''}`}
        </Text>
      </Button>
    </Tooltip>
  );
}
